import { useCallback } from 'react';

import {
	ufoEventProvisioningReadinessLatency,
	ufoEventE2eProvisioningLatency,
	ufoEventProductSetupSystemLatency,
	ufoEventProductSetupLatency,
} from '@/components/signup/utils/ufo-events';
import { SignupUfoCustomData } from '@/components/signup/utils/ufo.types';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags as gates } from '@/utils/feature-flags/feature-flag-manifest';

type FinalProvisioningScreen = NonNullable<SignupUfoCustomData['finalProvisioningScreen']>;

export const useProvisioningLatencyUfo = () => {
	const isProvisioningLatencyUfoEnabled = checkFeatureFlagGate(
		gates.wacUfoEventProvisioningLatency,
	);
	const isSystemLatencyUfoEnabled = checkFeatureFlagGate(
		gates.wacUfoEventProvisioningBackendLatency,
	);

	const startE2eProvisioning = useCallback(
		(options: Parameters<typeof ufoEventE2eProvisioningLatency.start>[0]) => {
			if (isProvisioningLatencyUfoEnabled) {
				void ufoEventE2eProvisioningLatency.start(options);
			}
		},
		[isProvisioningLatencyUfoEnabled],
	);

	const startProductSetup = useCallback(
		(options: Parameters<typeof ufoEventProductSetupLatency.start>[0]) => {
			if (isProvisioningLatencyUfoEnabled) {
				void ufoEventProductSetupLatency.start(options);
			}
		},
		[isProvisioningLatencyUfoEnabled],
	);

	const completeProductSetup = useCallback(
		(finalProvisioningScreen: FinalProvisioningScreen) => {
			if (isProvisioningLatencyUfoEnabled) {
				void ufoEventProductSetupLatency.success({ customData: { finalProvisioningScreen } });
			}
		},
		[isProvisioningLatencyUfoEnabled],
	);

	const completeE2eProvisioning = useCallback(
		(finalProvisioningScreen?: FinalProvisioningScreen) => {
			if (isProvisioningLatencyUfoEnabled) {
				void ufoEventE2eProvisioningLatency.success({ customData: { finalProvisioningScreen } });
			}
		},
		[isProvisioningLatencyUfoEnabled],
	);

	const completeAll = useCallback(
		(finalProvisioningScreen: FinalProvisioningScreen) => {
			completeProductSetup(finalProvisioningScreen);
			completeE2eProvisioning(finalProvisioningScreen);
		},
		[completeProductSetup, completeE2eProvisioning],
	);

	const startProvisioningReadiness = useCallback(
		(options: Parameters<typeof ufoEventProvisioningReadinessLatency.start>[0]) => {
			if (isSystemLatencyUfoEnabled) {
				void ufoEventProvisioningReadinessLatency.start(options);
			}
		},
		[isSystemLatencyUfoEnabled],
	);

	const completeProvisioningReadiness = useCallback(
		(finalProvisioningScreen?: FinalProvisioningScreen) => {
			if (isSystemLatencyUfoEnabled) {
				void ufoEventProvisioningReadinessLatency.success({
					customData: { finalProvisioningScreen },
				});
			}
		},
		[isSystemLatencyUfoEnabled],
	);

	const startProductSetupSystem = useCallback(
		(options: Parameters<typeof ufoEventProductSetupSystemLatency.start>[0]) => {
			if (isSystemLatencyUfoEnabled) {
				void ufoEventProductSetupSystemLatency.start(options);
			}
		},
		[isSystemLatencyUfoEnabled],
	);

	const pauseProductSetupSystem = useCallback(() => {
		if (isSystemLatencyUfoEnabled) {
			ufoEventProductSetupSystemLatency.pause();
		}
	}, [isSystemLatencyUfoEnabled]);

	const resumeProductSetupSystem = useCallback(
		(options: Parameters<typeof ufoEventProductSetupSystemLatency.start>[0]) => {
			if (isSystemLatencyUfoEnabled) {
				ufoEventProductSetupSystemLatency.resume();
				// Start a measurement if none is active.
				// UFO ignores this call when a measurement is already running.
				void ufoEventProductSetupSystemLatency.start(options);
			}
		},
		[isSystemLatencyUfoEnabled],
	);

	const completeProductSetupSystem = useCallback(
		(finalProvisioningScreen: FinalProvisioningScreen) => {
			if (isSystemLatencyUfoEnabled) {
				void ufoEventProductSetupSystemLatency.success({ customData: { finalProvisioningScreen } });
			}
		},
		[isSystemLatencyUfoEnabled],
	);

	return {
		startE2eProvisioning,
		startProductSetup,
		completeProductSetup,
		completeE2eProvisioning,
		completeAll,
		isProvisioningLatencyUfoEnabled,
		startProvisioningReadiness,
		completeProvisioningReadiness,
		startProductSetupSystem,
		pauseProductSetupSystem,
		resumeProductSetupSystem,
		completeProductSetupSystem,
	};
};
